import { useMemo } from 'react';
import {
  useRecentVisits,
  type VisitRow,
  type WeekHeatmap,
} from '@busy/api';
import type { ForecastDay } from './weather';
import { toLocalISODate } from './useWeatherByDate';

// 7-day capacity forecast — what a barber can plan against for the
// upcoming week. Deliberately simple model for v1:
//
//   1. Look back 8 weeks (56 days) of visits.
//   2. Bucket by day-of-week. For each DoW compute:
//        - mean visits/day (when the day had any visits)
//        - mean hours/day (sum of duration_min ÷ 60 across visits)
//        - mean revenue/day (sum of price_cents + tip_cents)
//        - sample size = # of distinct dates contributing to that DoW
//   3. For each upcoming forecast day:
//        - Read the matching DoW baseline
//        - Apply a weather adjustment multiplier (storm / heavy rain
//          / heavy snow → 0.7; rain → 0.9; otherwise 1.0)
//        - Confidence label from sample size:
//             HIGH   (8+ same-DoW samples — ~full 8-week window)
//             MEDIUM (4–7)
//             LOW    (<4 — too thin to plan against)
//
// Why this model and not something fancier:
//   - 8 weeks of same-DoW data is the smallest window where weekly
//     seasonality stops dominating noise. Less, the bands are wider
//     than the prediction.
//   - Weather multipliers come from public data on storms cutting
//     foot-traffic-driven retail by ~30%. v1.1 will switch to learning
//     each barber's *own* weather sensitivity once we have 90+ days of
//     concurrent visit + weather data per user.
//   - No-shows aren't in the model — we don't track them yet (deferred
//     to v1.1 when booking integration ships). Walk-in % from
//     was_appointment will widen the band on high-walk-in barbers
//     once enough rows accumulate, but for now the same band applies
//     to everyone.
//   - Hour-of-day decomposition is intentionally left out of this v1
//     hook. The Week At A Glance heatmap already surfaces hourly
//     utilization patterns; replicating that math here would just be
//     duplicate UI for the same insight. Forecast = day-level only.

const LOOKBACK_DAYS = 56;
const MIN_SAMPLES_HIGH = 8;
const MIN_SAMPLES_MEDIUM = 4;

/** Threshold above which we flag a forecast day as "walk-in-driven" —
 *  shows a tag on the card and widens the confidence band (walk-in
 *  demand is more weather-sensitive + harder to predict than booked
 *  appointments). 60% feels right empirically: anything above is
 *  meaningfully walk-in-tilted, below is mixed-or-appointment-tilted. */
const WALKIN_DRIVEN_THRESHOLD = 0.6;

/** Maximum stddev widening applied to walk-in-driven days. A 100%
 *  walk-in day gets +40% band width; a 0% walk-in day stays at the
 *  raw historical stddev. Linear in between. */
const STD_WIDEN_PER_WALKIN_FRACTION = 0.4;

/** Default walk-in fraction when we have no tagging data yet. Set
 *  high (1.0) so the forecast model errs on the side of FULL weather
 *  sensitivity + wider bands until the barber tags enough cuts to
 *  learn their actual mix. This means an untagged barber gets the
 *  same forecast as today (no behavior change), and tagging visibly
 *  TIGHTENS the band as it teaches us they're appointment-heavy. */
const DEFAULT_WALKIN_FRACTION = 1.0;

export type ForecastConfidence = 'high' | 'medium' | 'low';

// ────────── source mix (walk-in vs appointment per DoW) ──────────

/** Per-day-of-week breakdown of how the barber's cuts split between
 *  walk-ins and appointments over the lookback window. Drives:
 *    1. SourceMixTile (visual "your typical week by source")
 *    2. CapacityForecast band widening + weather-mult interpolation
 *
 *  Visits with `was_appointment === null` (skipped picker /
 *  pre-migration) are excluded from BOTH numerator and denominator
 *  so the % reflects intentional tagging only. */
export type SourceMixDay = {
  walkin: number;
  appointment: number;
  /** walkin + appointment — visits where the barber tagged the cut. */
  tagged: number;
  /** All visits in the period (tagged + untagged). Useful for the
   *  "tag more cuts" empty-state hint on the tile. */
  total: number;
  /** walkin / tagged × 100, rounded. Null when tagged === 0. */
  walkinPct: number | null;
};

export type SourceMixByDow = Record<number, SourceMixDay>;

/** Pure helper: visits → per-DoW walk-in/appointment counts. */
export function computeSourceMixByDow(visits: VisitRow[]): SourceMixByDow {
  const out: SourceMixByDow = {};
  for (let dow = 0; dow < 7; dow++) {
    out[dow] = { walkin: 0, appointment: 0, tagged: 0, total: 0, walkinPct: null };
  }
  for (const v of visits) {
    const dow = new Date(v.started_at).getDay();
    const slot = out[dow]!;
    slot.total += 1;
    if (v.was_appointment === true) {
      slot.appointment += 1;
      slot.tagged += 1;
    } else if (v.was_appointment === false) {
      slot.walkin += 1;
      slot.tagged += 1;
    }
  }
  for (let dow = 0; dow < 7; dow++) {
    const s = out[dow]!;
    s.walkinPct = s.tagged > 0 ? Math.round((s.walkin / s.tagged) * 100) : null;
  }
  return out;
}

// ────────── hourly pattern by DoW (forecast → heatmap bridge) ──────────

/** Per-DoW × per-hour mean utilization, derived from the past weekly
 *  heatmaps. Used by the unified heatmap to color FUTURE rows: each
 *  forecast cell reads `hourlyByDow[dow][hourIdx]` × weather multiplier
 *  × walk-in adjustment. Skipped (returns empty array per DoW) when
 *  the lookback has no entries — caller renders the row as a stripe
 *  pattern in that case. */
export type HourlyByDow = Record<number, number[]>;

export function computeHourlyByDow(weeks: WeekHeatmap[]): HourlyByDow {
  const out: HourlyByDow = {};
  // Initialize 16-slot empty rows so callers can always index.
  for (let dow = 0; dow < 7; dow++) out[dow] = [];
  // Accumulators: per dow, sum + count per hour slot.
  const sums: Record<number, number[]> = {};
  const counts: Record<number, number[]> = {};
  for (let dow = 0; dow < 7; dow++) {
    sums[dow] = new Array(16).fill(0);
    counts[dow] = new Array(16).fill(0);
  }
  for (const week of weeks) {
    for (const day of week.days) {
      const dowSum = sums[day.dow];
      const dowCount = counts[day.dow];
      if (!dowSum || !dowCount) continue;
      const len = Math.min(day.hours.length, 16);
      for (let h = 0; h < len; h++) {
        dowSum[h] = (dowSum[h] ?? 0) + (day.hours[h] ?? 0);
        dowCount[h] = (dowCount[h] ?? 0) + 1;
      }
    }
  }
  for (let dow = 0; dow < 7; dow++) {
    const row: number[] = [];
    const dowSum = sums[dow];
    const dowCount = counts[dow];
    if (!dowSum || !dowCount) continue;
    for (let h = 0; h < 16; h++) {
      const c = dowCount[h] ?? 0;
      row.push(c > 0 ? (dowSum[h] ?? 0) / c : 0);
    }
    out[dow] = row;
  }
  return out;
}

/** Hook wrapper for the source-mix computation. Shares the underlying
 *  useRecentVisits cache with useCapacityForecast — both calls hit the
 *  same query key + window, so the network roundtrip happens once. */
export function useSourceMixByDow() {
  const recent = useRecentVisits(LOOKBACK_DAYS);
  const data = useMemo(() => {
    if (!recent.data) return null;
    return computeSourceMixByDow(recent.data);
  }, [recent.data]);
  return { data, loading: recent.loading };
}

export type ForecastDayPlan = {
  /** YYYY-MM-DD, local timezone. Matches `ForecastDay.date`. */
  date: string;
  /** 0=Sun .. 6=Sat */
  dayOfWeek: number;
  weather: ForecastDay;
  weatherMultiplier: number;
  /** Historical walk-in fraction for this DoW (0–1). Used both to
   *  scale the weather multiplier (appointment-heavy days are less
   *  weather-sensitive) and to widen the confidence band (walk-in
   *  days are noisier). Defaults to 1.0 when the barber hasn't
   *  tagged any same-DoW cuts yet (conservative — full impact). */
  walkinFraction: number;
  /** True when walkinFraction > WALKIN_DRIVEN_THRESHOLD. Surfaces as
   *  a "Walk-in driven" tag on the forecast card. */
  walkinDriven: boolean;
  /** Distinct same-DoW dates contributing to the baseline. Drives the
   *  `confidence` label and is exposed for the explainer sheet. */
  sampleSize: number;
  confidence: ForecastConfidence;
  /** Expected visits — point estimate (multiplier-adjusted DoW mean).
   *  `low`/`high` form a ±1 stddev band around the mean, also
   *  multiplier-adjusted, clamped at 0 on the low end. */
  expectedVisits: number;
  visitsLow: number;
  visitsHigh: number;
  /** Same shape as `expectedVisits` but in cents. The forecast tile
   *  displays "$X – $Y" using these two as the band. */
  expectedRevenueCents: number;
  revenueLowCents: number;
  revenueHighCents: number;
  /** Expected billable hours of cutting (sum of duration_min ÷ 60
   *  for the DoW). Used to flag forecast days that exceed the
   *  barber's `daily_target_hours` ("over capacity"). */
  expectedHours: number;
  /** Synthesized 16-hour utilization row for this forecast day —
   *  same shape as `DayHeatmap.hours`. Computed by averaging the
   *  matching DoW's historical hourly pattern over the lookback
   *  window, then scaling each hour by the weather + walk-in
   *  multiplier. Lets the unified weekly heatmap render forecast
   *  cells with the same color gradient as past cells (just
   *  desaturated to signal "predicted"). Empty array when we have
   *  no historical hourly pattern for this DoW. */
  hours: number[];
};

export type CapacityForecast = {
  /** Plans for the 7 forecast days. Same order as the input forecast. */
  days: ForecastDayPlan[];
  /** True when the lookback window has too few visits overall to
   *  build *any* baseline (under 14 visits in 8 weeks). The component
   *  renders an "earn more data" empty state in this case. */
  insufficient: boolean;
  /** Median sample size across the 7 days. Surfaced in the explainer
   *  sheet copy ("Based on N same-day samples"). */
  medianSampleSize: number;
  /** Per-DoW walk-in / appointment counts the forecast was built on.
   *  Exposed so the caller (CapacityTile) can wire SourceMixTile to
   *  the SAME numbers — no risk of the forecast band and the visual
   *  tile drifting out of sync. */
  sourceMix: SourceMixByDow;
};

/** Buckets visits by day-of-week and computes per-DoW means + stddevs.
 *  Public for testing; the hook itself wraps this with a data fetch.
 *
 *  `hourlyByDow` is optional — when omitted, forecast days come back
 *  with empty `hours[]` arrays (the unified heatmap will fall back to
 *  a stripe pattern for those rows). Passed in when the caller has
 *  weekly heatmaps available (the CapacityTile path). */
export function computeForecast(
  visits: VisitRow[],
  forecast: ForecastDay[],
  hourlyByDow?: HourlyByDow,
): CapacityForecast {
  // Bucket: dow → { date → { visits, hours, revenue_cents } }.
  // Day key is local YYYY-MM-DD so day-boundary visits group correctly
  // regardless of started_at's UTC offset.
  const byDowByDate: Record<number, Record<string, {
    visits: number;
    hours: number;
    revenue_cents: number;
  }>> = {};
  for (let dow = 0; dow < 7; dow++) byDowByDate[dow] = {};

  for (const v of visits) {
    const d = new Date(v.started_at);
    const dow = d.getDay();
    const date = toLocalISODate(d);
    const bucket = byDowByDate[dow]!;
    const entry = bucket[date] ?? { visits: 0, hours: 0, revenue_cents: 0 };
    entry.visits += 1;
    entry.hours += (v.duration_min ?? 0) / 60;
    entry.revenue_cents += (v.price_cents ?? 0) + (v.tip_cents ?? 0);
    bucket[date] = entry;
  }

  // Per-DoW aggregate stats.
  type DowStats = {
    sampleSize: number;
    meanVisits: number;
    stdVisits: number;
    meanHours: number;
    stdHours: number;
    meanRevenueCents: number;
    stdRevenueCents: number;
  };
  const dowStats: Record<number, DowStats> = {};
  for (let dow = 0; dow < 7; dow++) {
    const dates = Object.values(byDowByDate[dow]!);
    const n = dates.length;
    if (n === 0) {
      dowStats[dow] = {
        sampleSize: 0,
        meanVisits: 0, stdVisits: 0,
        meanHours: 0, stdHours: 0,
        meanRevenueCents: 0, stdRevenueCents: 0,
      };
      continue;
    }
    const meanVisits = dates.reduce((s, d) => s + d.visits, 0) / n;
    const meanHours = dates.reduce((s, d) => s + d.hours, 0) / n;
    const meanRevenueCents = dates.reduce((s, d) => s + d.revenue_cents, 0) / n;
    const stdVisits = stdDev(dates.map((d) => d.visits), meanVisits);
    const stdHours = stdDev(dates.map((d) => d.hours), meanHours);
    const stdRevenueCents = stdDev(dates.map((d) => d.revenue_cents), meanRevenueCents);
    dowStats[dow] = {
      sampleSize: n,
      meanVisits, stdVisits,
      meanHours, stdHours,
      meanRevenueCents, stdRevenueCents,
    };
  }

  // Source mix per DoW — feeds both the walk-in-aware weather
  // multiplier (interpolate base population multiplier by walk-in
  // fraction) AND the stddev widening below. Computed once here so
  // the caller can read the same numbers off `sourceMix` without
  // re-walking the visits.
  const sourceMix = computeSourceMixByDow(visits);

  const days: ForecastDayPlan[] = forecast.map((wx) => {
    // Parse forecast date as a local-midnight Date so getDay() returns
    // the right DoW regardless of UTC offset. "2026-05-13" parsed by
    // `new Date(str)` is interpreted as UTC; we need local.
    const [y, m, d] = wx.date.split('-').map((n) => parseInt(n, 10));
    const localDate = new Date(y!, (m ?? 1) - 1, d ?? 1);
    const dow = localDate.getDay();
    const stats = dowStats[dow]!;

    // Walk-in fraction for this DoW — when the barber hasn't tagged
    // any same-DoW cuts yet, fall back to the conservative default
    // (full weather impact + max widening). Once they tag enough
    // cuts to back the % up, the model honors their actual mix.
    const mixPct = sourceMix[dow]?.walkinPct;
    const walkinFraction = mixPct != null ? mixPct / 100 : DEFAULT_WALKIN_FRACTION;
    const walkinDriven = walkinFraction > WALKIN_DRIVEN_THRESHOLD;

    // Walk-in-aware weather multiplier — interpolates the population
    // base multiplier toward 1.0 as walk-in % drops. 100% walk-in →
    // full storm/rain hit; 0% walk-in → no weather impact (clients
    // are already booked, they show up regardless).
    const mult = weatherMultiplier(wx, walkinFraction);

    // Band widening — walk-in days are noisier (no commitment, easy
    // to skip). Stretch the ±1σ band by up to 40% on a 100% walk-in
    // day; leave it untouched on a 0% walk-in day.
    const widenFactor = 1 + STD_WIDEN_PER_WALKIN_FRACTION * walkinFraction;
    const widenedStdVisits = stats.stdVisits * widenFactor;
    const widenedStdRevenue = stats.stdRevenueCents * widenFactor;

    const visitsLow = Math.max(0, Math.round((stats.meanVisits - widenedStdVisits) * mult));
    const visitsHigh = Math.max(0, Math.round((stats.meanVisits + widenedStdVisits) * mult));
    const expectedVisits = Math.round(stats.meanVisits * mult * 10) / 10;
    const revenueLowCents = Math.max(0, Math.round((stats.meanRevenueCents - widenedStdRevenue) * mult));
    const revenueHighCents = Math.max(0, Math.round((stats.meanRevenueCents + widenedStdRevenue) * mult));
    const expectedRevenueCents = Math.round(stats.meanRevenueCents * mult);
    const expectedHours = Math.round(stats.meanHours * mult * 10) / 10;

    // Synthesize the 16-cell hours[] row for this forecast day so the
    // unified weekly heatmap can color cells using the same gradient
    // as past actuals. Each cell = historical mean for that DoW × hour,
    // scaled by the same weather/walk-in multiplier we use elsewhere.
    // Empty array signals "no hourly pattern" → the heatmap renders the
    // row as a "predicted but no shape" stripe pattern.
    const hourlyBaseline = hourlyByDow?.[dow] ?? [];
    const hours: number[] = hourlyBaseline.length === 16
      ? hourlyBaseline.map((pct) => Math.max(0, pct * mult))
      : [];

    return {
      date: wx.date,
      dayOfWeek: dow,
      weather: wx,
      weatherMultiplier: mult,
      walkinFraction,
      walkinDriven,
      sampleSize: stats.sampleSize,
      confidence:
        stats.sampleSize >= MIN_SAMPLES_HIGH
          ? 'high'
          : stats.sampleSize >= MIN_SAMPLES_MEDIUM
          ? 'medium'
          : 'low',
      expectedVisits,
      visitsLow,
      visitsHigh,
      expectedRevenueCents,
      revenueLowCents,
      revenueHighCents,
      expectedHours,
      hours,
    };
  });

  const samples = days.map((d) => d.sampleSize).sort((a, b) => a - b);
  const medianSampleSize = samples.length > 0 ? samples[Math.floor(samples.length / 2)] ?? 0 : 0;

  return {
    days,
    insufficient: visits.length < 14,
    medianSampleSize,
    sourceMix,
  };
}

// ────────── helpers ──────────

function stdDev(values: number[], mean: number): number {
  if (values.length < 2) return 0;
  const variance =
    values.reduce((s, v) => s + (v - mean) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
}

/** Multiplier applied to the DoW baseline based on the forecast day's
 *  weather AND the barber's historical walk-in fraction for that DoW.
 *
 *  Why walk-in fraction matters: weather hits walk-in demand harder
 *  than appointment demand. Booked clients still show up to their
 *  rainy-Tuesday haircut; walk-ins decide last-minute and skip.
 *  Population-level multipliers (storms reduce foot traffic ~35%) are
 *  derived from retail studies that average across BOTH appointment
 *  and walk-in retail — applying them flat to an appointment-heavy
 *  barber over-penalizes weather.
 *
 *  Math: the population multiplier is treated as the "100% walk-in"
 *  endpoint. We interpolate toward 1.0 (no impact) as the walk-in
 *  fraction drops to 0:
 *      adjusted_mult = 1 − (1 − base_mult) × walkinFraction
 *
 *  Examples for a storm day (base = 0.65):
 *      walkin=1.0 → 0.65 (full -35% hit)
 *      walkin=0.5 → 0.825 (-17.5% hit, half-impact)
 *      walkin=0.0 → 1.00 (no impact — all appointments hold)
 *
 *  Base multipliers come from public retail-foot-traffic studies;
 *  v1.1 will replace these with a per-user learned coefficient once
 *  we have 90+ days of side-by-side visit + weather data per barber.
 *
 *  - storm (severe weather: thunderstorms, severe storms)        → 0.65
 *  - rainy w/ alert (heavy rain, freezing rain, heavy showers)   → 0.75
 *  - snow w/ alert (heavy snow)                                  → 0.70
 *  - regular rain                                                → 0.90
 *  - regular snow                                                → 0.85
 *  - everything else (sunny, partly cloudy, cloudy, fog, heat)   → 1.00
 */
export function weatherMultiplier(
  day: ForecastDay,
  walkinFraction: number = DEFAULT_WALKIN_FRACTION,
): number {
  let base: number;
  if (day.condition === 'storm') base = 0.65;
  else if (day.condition === 'rainy' && day.alert) base = 0.75;
  else if (day.condition === 'snow' && day.alert) base = 0.7;
  else if (day.condition === 'rainy') base = 0.9;
  else if (day.condition === 'snow') base = 0.85;
  else base = 1.0;
  // Interpolate base → 1.0 by walk-in fraction. Clamp the fraction
  // defensively to [0, 1] so a bad caller can't pull the multiplier
  // above 1 or below the base.
  const clamped = Math.max(0, Math.min(1, walkinFraction));
  return 1 - (1 - base) * clamped;
}

/** Hook wrapper: fetches the last 8 weeks of visits and returns the
 *  forecast plans.
 *
 *  `weeklyHeatmaps` is optional — when supplied (the CapacityTile path
 *  has them from useCapacityUtilization), each forecast day comes back
 *  with a synthesized `hours[]` row that lets the unified weekly
 *  heatmap render forecast cells. Without it, hours[] stays empty and
 *  the heatmap falls back to a stripe pattern for forecast rows. */
export function useCapacityForecast(
  forecast: ForecastDay[],
  weeklyHeatmaps?: WeekHeatmap[] | null,
): {
  data: CapacityForecast | null;
  loading: boolean;
} {
  const recent = useRecentVisits(LOOKBACK_DAYS);
  const hourlyByDow = useMemo(
    () => (weeklyHeatmaps ? computeHourlyByDow(weeklyHeatmaps) : undefined),
    [weeklyHeatmaps],
  );
  const data = useMemo(() => {
    if (recent.loading) return null;
    if (!recent.data) return null;
    return computeForecast(recent.data, forecast, hourlyByDow);
  }, [recent.loading, recent.data, forecast, hourlyByDow]);
  return { data, loading: recent.loading };
}
